import { useState, useEffect, useRef } from 'react';
import { useUsage, useCheckout, useBillingPortal } from '@/hooks/use-usage';
import { useTrackEvent } from '@/hooks/use-track-event';
import { Loader2, Zap, Film, CreditCard, Settings, ImageIcon } from 'lucide-react';

export function UsageDisplay() {
  const { data, isLoading, error } = useUsage();
  const checkout = useCheckout();
  const portal = useBillingPortal();
  const { track } = useTrackEvent();

  // Delayed skeleton pattern - ensure skeleton shows for at least 2 seconds
  const [showSkeleton, setShowSkeleton] = useState(true);
  const loadingStarted = useRef<number | null>(null);

  useEffect(() => {
    if (isLoading) {
      loadingStarted.current = Date.now();
      setShowSkeleton(true);
    } else {
      const elapsed = Date.now() - (loadingStarted.current || Date.now());
      const remaining = Math.max(0, 1000 - elapsed);
      const timer = setTimeout(() => setShowSkeleton(false), remaining);
      return () => clearTimeout(timer);
    }
  }, [isLoading]);

  if (showSkeleton) {
    return (
      <div className="space-y-3">
        {/* Header skeleton - Usage + Plan badge */}
        <div className="flex items-center justify-between">
          <div className="h-4 w-12 animate-pulse rounded bg-slate-200" />
          <div className="h-5 w-10 animate-pulse rounded-full bg-slate-200" />
        </div>

        {/* Videos row skeleton - actual height: 20px */}
        <div className="flex items-center justify-between h-5">
          <div className="flex items-center gap-2">
            <div className="h-3.5 w-3.5 animate-pulse rounded bg-slate-200" />
            <div className="h-3 w-12 animate-pulse rounded bg-slate-200" />
          </div>
          <div className="flex items-center gap-2">
            <div className="h-4 w-12 animate-pulse rounded bg-slate-200" />
            <div className="h-3 w-10 animate-pulse rounded bg-slate-200" />
          </div>
        </div>

        {/* Videos progress bar */}
        <div className="h-1.5 w-full animate-pulse rounded-full bg-slate-100" />

        {/* Image Analysis row skeleton - actual height: 20px */}
        <div className="flex items-center justify-between h-5">
          <div className="flex items-center gap-2">
            <div className="h-3.5 w-3.5 animate-pulse rounded bg-slate-200" />
            <div className="h-3 w-20 animate-pulse rounded bg-slate-200" />
          </div>
          <div className="flex items-center gap-2">
            <div className="h-4 w-10 animate-pulse rounded bg-slate-200" />
            <div className="h-3 w-10 animate-pulse rounded bg-slate-200" />
          </div>
        </div>

        {/* Image Analysis progress bar */}
        <div className="h-1.5 w-full animate-pulse rounded-full bg-slate-100" />

        {/* Tokens row skeleton - actual height: 16px */}
        <div className="flex items-center justify-between text-xs h-4">
          <div className="flex items-center gap-1">
            <div className="h-3 w-3 animate-pulse rounded bg-slate-200" />
            <div className="h-3 w-16 animate-pulse rounded bg-slate-200" />
          </div>
          <div className="h-3 w-14 animate-pulse rounded bg-slate-200" />
        </div>

        {/* Button skeleton - matches actual button height (34px) */}
        <div className="h-[34px] w-full animate-pulse rounded-lg border border-slate-200 bg-slate-100" />
      </div>
    );
  }

  if (error || !data?.data) {
    // Fallback: Show upgrade button even when API fails
    return (
      <div className="space-y-3">
        {/* Inline usage info */}
        <div className="flex items-center justify-between py-1">
          <div className="flex items-center gap-2">
            <Film className="h-3.5 w-3.5 text-slate-400" />
            <span className="text-xs text-slate-500">Videos remaining</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-slate-700">3</span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
              Free
            </span>
          </div>
        </div>
        {/* Pro Plan Details */}
        <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
          <p className="text-xs font-medium text-slate-700">Pro Plan includes:</p>
          <ul className="space-y-1">
            <li className="flex items-center gap-2 text-xs text-slate-600">
              <span className="h-1 w-1 rounded-full bg-slate-400" />
              15 videos/month
            </li>
            <li className="flex items-center gap-2 text-xs text-slate-600">
              <span className="h-1 w-1 rounded-full bg-slate-400" />
              5 image analyses/month
            </li>
          </ul>
        </div>
        {/* Upgrade CTA - subtle */}
        <button
          type="button"
          onClick={async () => {
            await track('upgrade_click');
            checkout.mutate();
          }}
          disabled={checkout.isPending}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50"
        >
          {checkout.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <CreditCard className="h-3.5 w-3.5" />
          )}
          Upgrade to Pro - $49/mo
        </button>
      </div>
    );
  }

  const usage = data.data;
  const videoPercentage = Math.round((usage.videosGenerated / usage.videosLimit) * 100);
  const imagePercentage = usage.imageAnalysesLimit > 0
    ? Math.round((usage.imageAnalyses / usage.imageAnalysesLimit) * 100)
    : 0;

  return (
    <div className="space-y-3">
      {/* Header with Usage title and Plan badge */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">Usage</span>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            usage.plan === 'pro'
              ? 'bg-blue-50 text-blue-600'
              : 'bg-slate-100 text-slate-500'
          }`}
        >
          {usage.plan === 'pro' ? 'Pro' : 'Free'}
        </span>
      </div>

      {/* Videos usage row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Film className="h-3.5 w-3.5 text-slate-400" />
          <span className="text-xs text-slate-500">Videos</span>
        </div>
        <div className="flex items-center gap-2.5">
          <span className="text-sm font-medium text-slate-700">
            {usage.videosRemaining} left
          </span>
          <span className="text-xs text-slate-400">
            ({usage.videosGenerated}/{usage.videosLimit})
          </span>
        </div>
      </div>

      {/* Videos progress bar */}
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full rounded-full transition-all ${
            videoPercentage > 90
              ? 'bg-red-500'
              : videoPercentage > 70
                ? 'bg-amber-500'
                : 'bg-blue-500'
          }`}
          style={{ width: `${Math.min(videoPercentage, 100)}%` }}
        />
      </div>

      {/* Image Analysis usage row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ImageIcon className="h-3.5 w-3.5 text-slate-400" />
          <span className="text-xs text-slate-500">Image Analysis</span>
        </div>
        <div className="flex items-center gap-2.5">
          <span className="text-sm font-medium text-slate-700">
            {usage.imageAnalysesRemaining} left
          </span>
          <span className="text-xs text-slate-400">
            ({usage.imageAnalyses}/{usage.imageAnalysesLimit})
          </span>
        </div>
      </div>

      {/* Image Analysis progress bar */}
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
        <div
          className={`h-full rounded-full transition-all ${
            imagePercentage > 90
              ? 'bg-red-500'
              : imagePercentage > 70
                ? 'bg-amber-500'
                : 'bg-purple-500'
          }`}
          style={{ width: `${Math.min(imagePercentage, 100)}%` }}
        />
      </div>

      {/* Tokens inline */}
      <div className="flex items-center justify-between text-xs text-slate-400">
        <span className="flex items-center gap-1">
          <Zap className="h-3 w-3" />
          Tokens used
        </span>
        <span>{usage.tokensUsed.toLocaleString()}</span>
      </div>

      {/* Actions - subtle styling */}
      {usage.plan === 'free' ? (
        <>
          {/* Pro Plan Details */}
          <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
            <p className="text-xs font-medium text-slate-700">Pro Plan includes:</p>
            <ul className="space-y-1">
              <li className="flex items-center gap-2 text-xs text-slate-600">
                <span className="h-1 w-1 rounded-full bg-slate-400" />
                15 videos/month
              </li>
              <li className="flex items-center gap-2 text-xs text-slate-600">
                <span className="h-1 w-1 rounded-full bg-slate-400" />
                5 image analyses/month
              </li>
            </ul>
          </div>
          <button
            type="button"
            onClick={async () => {
              await track('upgrade_click');
              checkout.mutate();
            }}
            disabled={checkout.isPending}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50"
          >
            {checkout.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <CreditCard className="h-3.5 w-3.5" />
            )}
            Upgrade to Pro - $49/mo
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={async () => {
            await track('manage_subscription_click');
            portal.mutate();
          }}
          disabled={portal.isPending}
          className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50"
        >
          {portal.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Settings className="h-3.5 w-3.5" />
          )}
          Manage Subscription
        </button>
      )}
    </div>
  );
}
